import React from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/Table';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { PER_PAGE } from '../api';

// Only http(s) URLs may be rendered as links (blocks javascript:, data:, etc.).
const isSafeUrl = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

const COLUMNS = ['#', 'Ссылка', 'GPT', 'Топ-1', 'Топ-2', 'Топ-3', 'Предсказанный', 'BPM', 'Контекст', 'Пол'];

const GENDER = {
  Мужской: { variant: 'primary', icon: 'bi-gender-male', label: 'М' },
  Женский: { variant: 'accent', icon: 'bi-gender-female', label: 'Ж' },
  Смешанный: { variant: 'default', icon: 'bi-people', label: 'См' },
};

/* Приглушённый прочерк для ячеек без данных (строки с ошибкой пайплайна). */
const Dash = () => (
  <span className="text-text-muted" title="Нет данных: ошибка пайплайна">—</span>
);

const SkeletonRow = ({ widths }) => (
  <TableRow>
    {widths.map((w, i) => (
      <TableCell key={i}>
        <div className="skeleton h-3" style={{ width: `${w}%` }} />
      </TableCell>
    ))}
  </TableRow>
);

const HeadRow = () => (
  <TableRow>
    {COLUMNS.map((h) => (
      <TableHead
        key={h}
        className={
          h === '#' ? 'w-[3%]' : h === 'Ссылка' ? 'w-[18%]' : h === 'BPM' ? 'w-[7%]' : h === 'Пол' ? 'w-[6%]' : ''
        }
      >
        {h}
      </TableHead>
    ))}
  </TableRow>
);

const LinkTable = ({ links, loading, currentPage, totalPages, onPageChange }) => {
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const windowSize = 5;
    let start = Math.max(1, currentPage - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    if (end - start + 1 < windowSize) start = Math.max(1, end - windowSize + 1);
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

    const PageBtn = ({ page }) => (
      <Button
        variant={page === currentPage ? 'primary' : 'ghost'}
        size="sm"
        className="tabular min-w-[32px] justify-center"
        aria-current={page === currentPage ? 'page' : undefined}
        aria-label={`Страница ${page}`}
        onClick={() => onPageChange(page)}
      >
        {page}
      </Button>
    );

    return (
      <nav className="mt-lg flex items-center justify-center gap-sm" aria-label="Навигация по страницам">
        <Button
          variant="ghost"
          size="icon"
          icon="bi-chevron-left"
          title="Предыдущая страница"
          aria-label="Предыдущая страница"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        />

        {/* Узкие экраны: компактный счётчик вместо ряда кнопок */}
        <span className="px-md text-body-sm text-text-muted sm:hidden">
          Стр. <span className="tabular text-text-strong">{currentPage}</span> из{' '}
          <span className="tabular">{totalPages}</span>
        </span>

        {/* Широкие экраны: номера страниц */}
        <div className="hidden items-center gap-sm sm:flex">
          {start > 1 && (
            <>
              <PageBtn page={1} />
              {start > 2 && (
                <span className="px-xs text-body-sm text-text-muted" aria-hidden="true">…</span>
              )}
            </>
          )}
          {pages.map((p) => (
            <PageBtn key={p} page={p} />
          ))}
          {end < totalPages && (
            <>
              {end < totalPages - 1 && (
                <span className="px-xs text-body-sm text-text-muted" aria-hidden="true">…</span>
              )}
              <PageBtn page={totalPages} />
            </>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          icon="bi-chevron-right"
          title="Следующая страница"
          aria-label="Следующая страница"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        />
      </nav>
    );
  };

  if (loading) {
    return (
      <div className="animate-fade-in rounded-md border border-border">
        <Table>
          <TableHeader>
            <HeadRow />
          </TableHeader>
          <TableBody>
            {Array.from({ length: 8 }, (_, i) => (
              <SkeletonRow key={i} widths={COLUMNS.map((_, c) => 40 + ((i * 13 + c * 29) % 55))} />
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (!links?.length) {
    return (
      <div className="animate-fade-in py-3xl text-center">
        <i className="bi bi-inbox text-kpi text-text-muted" aria-hidden="true"></i>
        <p className="mt-md text-body-sm text-text-muted">
          Нет данных для отображения — загрузите файл со ссылками
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Внутренний контейнер Table скроллит по обеим осям; ограничиваем его
          высоту, чтобы «липкая» шапка работала при вертикальном скролле. */}
      <div className="rounded-md border border-border [&>div]:max-h-[65vh]">
        <Table>
          <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10">
            <HeadRow />
          </TableHeader>
          <TableBody>
            {links.map((link, i) => {
              const hasError = Boolean(link.pipeline_error);
              const gender = GENDER[link.performer_gender];
              return (
                <TableRow key={`${link.url}-${i}`} className={hasError ? 'bg-destructive-soft/40' : ''}>
                  <TableCell className="tabular text-body-xs text-text-muted">
                    {(currentPage - 1) * PER_PAGE + i + 1}
                  </TableCell>
                  <TableCell>
                    {isSafeUrl(link.url) ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block max-w-[200px] truncate text-primary transition-colors duration-200 hover:text-primary-hover hover:underline xl:max-w-[260px]"
                        title={link.url}
                      >
                        {link.url}
                      </a>
                    ) : (
                      <span className="block max-w-[200px] truncate text-text-muted xl:max-w-[260px]" title={String(link.url ?? '')}>
                        {String(link.url ?? '')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{hasError ? <Dash /> : <Badge variant="outline">{link.mistral_genre}</Badge>}</TableCell>
                  <TableCell>{hasError ? <Dash /> : <Badge variant="primary">{link.music_genre}</Badge>}</TableCell>
                  <TableCell>{hasError ? <Dash /> : <Badge variant="accent">{link.drums_genre}</Badge>}</TableCell>
                  <TableCell>{hasError ? <Dash /> : <Badge variant="default">{link.bass_genre}</Badge>}</TableCell>
                  <TableCell>
                    {hasError ? (
                      <Badge
                        variant="destructive"
                        icon="bi-exclamation-triangle"
                        title={`Ошибка пайплайна: ${link.pipeline_error}`}
                      >
                        Ошибка
                      </Badge>
                    ) : (
                      <Badge variant="success" icon="bi-music-note">{link.final_genre}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="tabular text-body-sm text-text">
                    {hasError ? <Dash /> : link.bpm}
                  </TableCell>
                  <TableCell>
                    {hasError ? (
                      <Dash />
                    ) : (
                      <Badge variant="default" icon="bi-info-circle" title={link.context_description}>
                        {link.context}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {hasError || !gender ? (
                      <Dash />
                    ) : (
                      <Badge variant={gender.variant} icon={gender.icon} title={link.performer_gender}>
                        {gender.label}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {renderPagination()}

      <p className="mt-sm text-center text-body-xs text-text-muted">
        Страница <span className="tabular">{currentPage}</span> из <span className="tabular">{totalPages}</span> · по{' '}
        <span className="tabular">{PER_PAGE}</span> элементов
      </p>
    </div>
  );
};

export default LinkTable;
